/**
 * KraftStories — REVIEWS ANALYTICS
 * ============================================================================
 * GA4 instrumentation for the in-house reviews system (assets/reviews-widget.js,
 * API at reviews.kraftstories.com).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * reviews-widget.js contains no analytics of any kind — grepping it for
 * dataLayer/gtag/fbq/dispatchEvent returns only a local variable named `track`
 * (a scroller element). So review reading, filtering, photo viewing and helpful
 * votes have all been invisible, exactly as booking and gifting were before
 * session-analytics.js.
 *
 * WHY A WRAPPER AND NOT EDITS TO THE SOURCE
 * -----------------------------------------
 * Same reasoning as session-analytics.js. The widget exposes no global — it
 * stashes its instance on `root.__ksr` and drives everything through delegated
 * document listeners. There is nothing to wrap, so this file listens on the
 * same public contract the widget itself uses: the `data-ksr-*` attributes.
 * Those are a more stable surface than class names, and leaving the widget
 * untouched means ongoing work on it cannot conflict with tracking.
 *
 * RULES THIS FILE OBEYS
 * ---------------------
 * 1. **Never send PII.** Review bodies, reviewer names and photo URLs are
 *    personal data. Only review ids, counts, ratings and booleans are sent.
 * 2. **Never break the page.** Every handler is wrapped; a throw here must not
 *    stop the widget from rendering reviews.
 * 3. **Capture phase, passive.** Listeners never preventDefault and never stop
 *    propagation, so the widget's own delegated handlers are unaffected.
 * 4. **Emit through __kraftGa4Mirror**, like session-analytics.js, so there is
 *    one definition of an event payload rather than two.
 *
 * THE POINT OF ALL THIS
 * ---------------------
 * The business question is not "how many people read reviews" — it is whether
 * reading reviews makes someone more likely to book. `review_engaged` fires once
 * per session on the first genuine interaction, which gives GA4 a clean flag to
 * segment `booking_confirm` and `purchase` against. That comparison is the
 * reason this file is worth having.
 *
 * Debug with ?ks_debug=1 — every event is logged with its params before queueing.
 */

(function () {
  'use strict';

  if (window.__kraftReviewsAnalyticsLoaded) return;
  window.__kraftReviewsAnalyticsLoaded = true;

  var DEBUG = /[?&]ks_debug=1/.test(window.location.search);

  // ─── emit ──────────────────────────────────────────────────────────────────
  // Mirrors session-analytics.js: __kraftGa4Mirror appears when
  // analytics-events.js runs, and this file may load first, so queue rather
  // than drop the opening events of the session.

  var queue = [];
  var flushTimer = null;

  function drain() {
    if (typeof window.__kraftGa4Mirror !== 'function') return false;
    while (queue.length) {
      var q = queue.shift();
      try { window.__kraftGa4Mirror(q[0], q[1]); } catch (e) { /* never break the page */ }
    }
    return true;
  }

  function emit(name, params) {
    var payload = params || {};
    payload.page_path = window.location.pathname;
    if (window.__kraft_page_category) payload.page_category = window.__kraft_page_category;
    if (window.__kraft_page_location_city) payload.page_location_city = window.__kraft_page_location_city;

    if (DEBUG) console.log('[reviews-analytics]', name, payload);

    queue.push([name, payload]);
    if (drain()) return;
    if (flushTimer) return;
    var tries = 0;
    flushTimer = setInterval(function () {
      if (drain() || ++tries > 60) { clearInterval(flushTimer); flushTimer = null; }
    }, 250);
  }

  // ─── helpers ───────────────────────────────────────────────────────────────

  /* Fires `review_engaged` at most once per session. This is the flag worth
     segmenting booking conversion on, so it must mean "deliberately interacted
     with reviews" — not "a reviews block happened to exist on the page". Scroll
     into view therefore does NOT count; only the actions below do. */
  var engagedThisPage = false;
  function markEngaged(action) {
    if (engagedThisPage) return;
    engagedThisPage = true;
    var firstInSession = true;
    try {
      if (window.sessionStorage.getItem('ks_review_engaged') === '1') firstInSession = false;
      else window.sessionStorage.setItem('ks_review_engaged', '1');
    } catch (e) { /* private mode / storage disabled — treat as first */ }
    emit('review_engaged', { first_action: action, first_in_session: firstInSession });
  }

  function num(value) {
    var n = Number(value);
    return isFinite(n) ? n : undefined;
  }

  /* Review ids are opaque integers from our own API, not personal data. */
  function reviewId(el, attr) {
    var raw = el && el.getAttribute(attr);
    return raw ? String(raw).slice(0, 32) : undefined;
  }

  function blockMeta(root) {
    if (!root) return {};
    var list = root.querySelector('[data-ksr-list]');
    return {
      product_id: root.getAttribute('data-ksr-product-id') || undefined,
      rating_avg: num(root.getAttribute('data-ksr-rating')),
      reviews_shown: list ? list.querySelectorAll('[data-ksr-review]').length : undefined
    };
  }

  // ─── block impressions ─────────────────────────────────────────────────────
  // Answers "was the reviews block actually seen", which a page_view cannot.
  // Half the block visible for 1s, once per element.

  function watchBlocks() {
    var roots = document.querySelectorAll('[data-ksr-root]');
    if (!roots.length) return;

    if (!('IntersectionObserver' in window)) {
      // No observer: count them as seen rather than losing the metric entirely,
      // and say so in the payload so the two are never silently mixed.
      Array.prototype.forEach.call(roots, function (root) {
        var meta = blockMeta(root);
        meta.visibility = 'assumed';
        emit('review_block_view', meta);
      });
      return;
    }

    var timers = new WeakMap();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target;
        if (entry.isIntersecting) {
          if (timers.get(el)) return;
          timers.set(el, setTimeout(function () {
            observer.unobserve(el);
            var meta = blockMeta(el);
            meta.visibility = 'observed';
            emit('review_block_view', meta);
          }, 1000));
        } else {
          var t = timers.get(el);
          if (t) { clearTimeout(t); timers.delete(el); }
        }
      });
    }, { threshold: 0.5 });

    Array.prototype.forEach.call(roots, function (root) { observer.observe(root); });
  }

  // ─── interactions ──────────────────────────────────────────────────────────

  document.addEventListener('click', function (event) {
    try {
      var t = event.target;
      if (!t || !t.closest) return;
      var el;

      /* Helpful vote — the strongest signal a review was actually read. */
      el = t.closest('[data-ksr-helpful]');
      if (el) {
        markEngaged('helpful');
        emit('review_helpful', { review_id: reviewId(el, 'data-ksr-helpful') });
        return;
      }

      /* Photo opened in the lightbox. The URL is deliberately not sent. */
      el = t.closest('[data-ksr-photo]');
      if (el) {
        markEngaged('photo');
        emit('review_photo_open', {
          review_id: reviewId(el.closest('[data-ksr-review]'), 'data-ksr-review'),
          has_photo: true
        });
        return;
      }

      /* Rating filter. */
      el = t.closest('[data-ksr-filter]');
      if (el) {
        markEngaged('filter');
        emit('review_filter', { rating_filter: el.getAttribute('data-ksr-filter') || 'all' });
        return;
      }

      /* "Show more reviews" — depth of reading. */
      el = t.closest('[data-ksr-more]');
      if (el) {
        markEngaged('more');
        var root = el.closest('[data-ksr-root]');
        var meta = blockMeta(root);
        emit('review_more', { product_id: meta.product_id, reviews_shown_before: meta.reviews_shown });
        return;
      }

      /* "Read all reviews" / "Browse photos" modal openers. */
      el = t.closest('[data-ksr-open]');
      if (el) {
        markEngaged('open_modal');
        emit('review_modal_open', {
          open_kind: el.getAttribute('data-ksr-open') || undefined,
          product_id: el.getAttribute('data-ksr-product-id') || undefined,
          photo_count: num(el.getAttribute('data-ksr-photos'))
        });
        return;
      }

      /* Product tab — only counts when inside a tab group, matching the
         widget's own condition, so a product id elsewhere is not mistaken
         for a tab click. */
      el = t.closest('[data-ksr-product-id]');
      if (el && el.closest('[data-ksr-tabs]')) {
        markEngaged('tab');
        emit('review_tab_select', { product_id: el.getAttribute('data-ksr-product-id') || undefined });
        return;
      }

      /* Photo-wall arrows. Engagement, but weak — deliberately does not
         set review_engaged, which is reserved for intent. */
      el = t.closest('[data-ksr-scroll]');
      if (el) {
        emit('review_wall_scroll', { direction: num(el.getAttribute('data-ksr-scroll')) });
      }
    } catch (e) { /* never break the page */ }
  }, true);

  /* Sort is a <select> in current markup but has been a button group before,
     so both paths are handled. */
  document.addEventListener('change', function (event) {
    try {
      var el = event.target && event.target.closest && event.target.closest('[data-ksr-sort]');
      if (!el) return;
      markEngaged('sort');
      emit('review_sort', { sort_by: (el.value || el.getAttribute('data-ksr-sort') || '').slice(0, 40) });
    } catch (e) { /* never break the page */ }
  }, true);

  // ─── init ──────────────────────────────────────────────────────────────────

  function init() {
    try { watchBlocks(); } catch (e) { /* never break the page */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
